/* import __COLOCATED_TEMPLATE__ from './step-wrapper.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import { type SafeString } from '@ember/template/-private/handlebars';

interface Args {
  step?: number;
  showSideDrawer: boolean;
  toggleSideDrawer: () => void;
  setStep: (step: number) => void;
}

export default class StandaloneSalesforceArticleSyncStepWrapper extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  get steps(): {
    title: string;
    description: string | SafeString;
    component:
      | 'standalone/salesforce/setup/article-sync/steps/connect'
      | 'standalone/salesforce/setup/article-sync/steps/filter'
      | 'standalone/salesforce/setup/article-sync/steps/map';
  }[] {
    return [
      {
        title: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.title',
        ),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.description',
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/connect',
      },
      {
        title: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.filter.title',
        ),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.filter.description',
          {
            instanceUrl: this.salesforceConfig.instanceUrl,
          },
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/filter',
      },
      {
        title: this.intl.t('standalone.salesforce.setup.article-sync.step-wrapper.steps.map.title'),
        description: this.intl.t(
          'standalone.salesforce.setup.article-sync.step-wrapper.steps.map.description',
          {
            audienceUrl: this.router.urlFor('apps.app.settings.data.audiences'),
            htmlSafe: true,
          },
        ),
        component: 'standalone/salesforce/setup/article-sync/steps/map',
      },
    ];
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get currentStep() {
    return this.args.step || 0;
  }

  @action nextStep() {
    if (this.currentStep < this.steps.length - 1) {
      this.args.setStep(this.currentStep + 1);
    }
  }

  @action previousStep() {
    if (this.currentStep > 0) {
      this.args.setStep(this.currentStep - 1);
    }
  }

  get currentStepData() {
    return this.steps[this.currentStep];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::StepWrapper': typeof StandaloneSalesforceArticleSyncStepWrapper;
    'standalone/salesforce/setup/article-sync/step-wrapper': typeof StandaloneSalesforceArticleSyncStepWrapper;
  }
}
